import { useEffect } from "react";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  LiveReload,
  useLoaderData
} from "@remix-run/react";
import type { LinksFunction, MetaFunction } from "@remix-run/node";
import { json } from "@remix-run/node";
import { Analytics } from "@vercel/analytics/remix";
import { initializeOptimizations, criticalCSS } from "./shared/utils/optimizations";
import { languageDetection, LANGUAGE_STORAGE_KEY, DEFAULT_LANGUAGE } from "./shared/utils/i18n";
import { ThemeProvider } from "./shared/components/ThemeProvider";
import { getAllKeywords } from "./shared/utils/seo-keywords";

import "./shared/components/tailwind.css";
import "./shared/components/unified-fonts.css";

export const meta: MetaFunction = () => {
  // دمج جميع الكلمات المفتاحية المحسنة
  const allKeywords = getAllKeywords();
  const keywordsString = allKeywords.join(', ');
  
  return [
    // العنوان الرئيسي المحسن لـ SEO
    { title: "أسامة الزبيدي - محلل بيانات، مطور ويب، متخصص رياضيات | Osama Al-Zubaidi - Data Analyst, Web Developer, Mathematics Specialist" },
    
    // الوصف المحسن لـ SEO
    { name: "description", content: "موقع أسامة الزبيدي الشخصي - محلل بيانات، مطور ويب، متخصص رياضيات. يوفر أدوات مجانية متنوعة: حاسبة العمر، حاسبة الفرق بين التواريخ، تفقيط الأعداد، واتس مباشر، مولد رمز الاستجابة السريعة، السيرة الذاتية الذكية، بطاقات التهنئة، أذكاري، محول التاريخ، تاريخ اليوم، ومولد الفواتير. أدوات عربية مجانية ومفيدة للجميع." },
    
    // الكلمات المفتاحية الشاملة المحسنة
    { name: "keywords", content: keywordsString },
    
    // معلومات المؤلف
    { name: "author", content: "أسامة الزبيدي - Osama Al-Zubaidi" },
    { name: "creator", content: "أسامة الزبيدي" },
    { name: "publisher", content: "أسامة الزبيدي" },
    { name: "copyright", content: "صنع بكل ❤️ في 🇸🇦" },
    
    // إعدادات الروبوتات المحسنة - Enhanced Robots Settings
    { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1, max-archive:1" },
    { name: "googlebot", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    { name: "bingbot", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    { name: "slurp", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    { name: "yandex", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    { name: "baiduspider", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    { name: "duckduckbot", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    { name: "facebookexternalhit", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    { name: "twitterbot", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    { name: "linkedinbot", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    { name: "whatsapp", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    
    // معلومات اللغة والموقع
    { name: "language", content: "Arabic, English" },
    { name: "geo.region", content: "SA" },
    { name: "geo.country", content: "Saudi Arabia" },
    { name: "geo.placename", content: "Saudi Arabia" },
    { name: "geo.position", content: "24.7136;46.6753" },
    { name: "ICBM", content: "24.7136, 46.6753" },
    
    // إعدادات العرض
    { name: "viewport", content: "width=device-width, initial-scale=1, shrink-to-fit=no" },
    { name: "theme-color", content: "#7c3aed" },
    { name: "msapplication-TileColor", content: "#7c3aed" },
    { name: "color-scheme", content: "light dark" },
    { name: "mobile-web-app-capable", content: "yes" },
    { name: "apple-mobile-web-app-capable", content: "yes" },
    { name: "apple-mobile-web-app-status-bar-style", content: "default" },
    { name: "format-detection", content: "telephone=no" },
    
    // معلومات التطبيق
    { name: "application-name", content: "أسامة الزبيدي - Osama Al-Zubaidi" },
    { name: "apple-mobile-web-app-title", content: "أسامة الزبيدي" },
    { name: "msapplication-TileImage", content: "/android-chrome-192x192.png" },
    { name: "msapplication-config", content: "/browserconfig.xml" },
    
    // Open Graph / Facebook - محسن لـ SEO
    { property: "og:type", content: "website" },
    { property: "og:title", content: "أسامة الزبيدي - محلل بيانات، مطور ويب، متخصص رياضيات | Osama Al-Zubaidi" },
    { property: "og:description", content: "موقع أسامة الزبيدي الشخصي - محلل بيانات، مطور ويب، متخصص رياضيات. يوفر أدوات مجانية متنوعة ومفيدة: حاسبة العمر، حاسبة الفرق بين التواريخ، تفقيط الأعداد، واتس مباشر، مولد QR، السيرة الذاتية الذكية، بطاقات التهنئة، أذكاري، ومولد الفواتير." },
    { property: "og:url", content: "https://oz.sa" },
    { property: "og:site_name", content: "أسامة الزبيدي - Osama Al-Zubaidi" },
    { property: "og:locale", content: "ar_AR" },
    { property: "og:locale:alternate", content: "en_US" },
    { property: "og:image", content: "https://oz.sa/logo-light.png" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: "أسامة الزبيدي - محلل بيانات، مطور ويب، متخصص رياضيات" },
    { property: "og:image:type", content: "image/png" },
    { property: "og:image:secure_url", content: "https://oz.sa/logo-light.png" },
    
    // معلومات إضافية لـ Open Graph لتحسين ظهور "أسامة الزبيدي"
    { property: "og:brand", content: "أسامة الزبيدي" },
    { property: "og:brand:name", content: "أسامة الزبيدي" },
    { property: "og:brand:alternate", content: "Osama Al-Zubaidi" },
    { property: "og:brand:description", content: "محلل بيانات، مطور ويب، متخصص رياضيات" },
    { property: "og:brand:slogan", content: "أستطيع تحويل الأرقام إلى ضحك، والكود إلى قهوة! ☕" },
    { property: "og:site:name", content: "أسامة الزبيدي" },
    { property: "og:site:owner", content: "أسامة الزبيدي" },
    { property: "og:site:author", content: "أسامة الزبيدي" },
    { property: "og:site:creator", content: "أسامة الزبيدي" },
    { property: "og:site:publisher", content: "أسامة الزبيدي" },
    
    // Twitter Cards - محسن لـ SEO
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "أسامة الزبيدي - محلل بيانات، مطور ويب، متخصص رياضيات | Osama Al-Zubaidi" },
    { name: "twitter:description", content: "موقع أسامة الزبيدي الشخصي - محلل بيانات، مطور ويب، متخصص رياضيات. أدوات مجانية متنوعة ومفيدة للجميع." },
    { name: "twitter:creator", content: "@osama_zubaidi" },
    { name: "twitter:site", content: "@osama_zubaidi" },
    { name: "twitter:image", content: "https://oz.sa/logo-light.png" },
    { name: "twitter:image:alt", content: "أسامة الزبيدي - محلل بيانات، مطور ويب، متخصص رياضيات" },
    { name: "twitter:domain", content: "oz.sa" },
    
    // معلومات إضافية لـ Twitter Cards لتحسين ظهور "أسامة الزبيدي"
    { name: "twitter:brand", content: "أسامة الزبيدي" },
    { name: "twitter:brand:name", content: "أسامة الزبيدي" },
    { name: "twitter:brand:alternate", content: "Osama Al-Zubaidi" },
    { name: "twitter:brand:description", content: "محلل بيانات، مطور ويب، متخصص رياضيات" },
    { name: "twitter:brand:slogan", content: "أستطيع تحويل الأرقام إلى ضحك، والكود إلى قهوة! ☕" },
    { name: "twitter:site:name", content: "أسامة الزبيدي" },
    { name: "twitter:site:owner", content: "أسامة الزبيدي" },
    { name: "twitter:site:author", content: "أسامة الزبيدي" },
    { name: "twitter:site:creator", content: "أسامة الزبيدي" },
    { name: "twitter:site:publisher", content: "أسامة الزبيدي" },
    
    // معلومات إضافية لـ SEO
    { name: "canonical", content: "https://oz.sa" },
    { name: "alternate", content: "https://oz.sa", "hreflang": "ar" },
    { name: "alternate", content: "https://oz.sa", "hreflang": "en" },
    { name: "alternate", content: "https://oz.sa", "hreflang": "x-default" },
    
    // معلومات إضافية لتحسين ظهور "أسامة الزبيدي"
    { name: "brand", content: "أسامة الزبيدي" },
    { name: "brand:name", content: "أسامة الزبيدي" },
    { name: "brand:alternate", content: "Osama Al-Zubaidi" },
    { name: "brand:description", content: "محلل بيانات، مطور ويب، متخصص رياضيات" },
    { name: "brand:slogan", content: "أستطيع تحويل الأرقام إلى ضحك، والكود إلى قهوة! ☕" },
    { name: "site:name", content: "أسامة الزبيدي" },
    { name: "site:owner", content: "أسامة الزبيدي" },
    { name: "site:author", content: "أسامة الزبيدي" },
    { name: "site:creator", content: "أسامة الزبيدي" },
    { name: "site:publisher", content: "أسامة الزبيدي" },
    { name: "site:brand", content: "أسامة الزبيدي" },
    { name: "site:brand:name", content: "أسامة الزبيدي" },
    { name: "site:brand:alternate", content: "Osama Al-Zubaidi" },
    { name: "site:brand:description", content: "محلل بيانات، مطور ويب، متخصص رياضيات" },
    { name: "site:brand:slogan", content: "أستطيع تحويل الأرقام إلى ضحك، والكود إلى قهوة! ☕" },
    
    // معلومات الأمان
    { name: "referrer", content: "strict-origin-when-cross-origin" },
    { name: "X-Content-Type-Options", content: "nosniff" },
    { name: "X-Frame-Options", content: "DENY" },
    { name: "X-XSS-Protection", content: "1; mode=block" },
    { name: "Content-Security-Policy", content: "default-src 'self'; script-src 'self' 'unsafe-inline' 'unsafe-eval' https://vercel.live; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; font-src 'self' https://fonts.gstatic.com; img-src 'self' data: https:; connect-src 'self' https://vercel.live;" },
    
    // معلومات إضافية للتحسين
    { name: "rating", content: "General" },
    { name: "distribution", content: "Global" },
    { name: "coverage", content: "Worldwide" },
    { name: "target", content: "all" },
    { name: "HandheldFriendly", content: "true" },
    { name: "MobileOptimized", content: "width" },
    { name: "apple-touch-fullscreen", content: "yes" },
    { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
  ];
};

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Tajawal:wght@200;300;400;500;700;800;900&family=Inter:wght@100;200;300;400;500;600;700;800;900&family=Segoe+UI:wght@300;400;500;600;700&display=swap",
  },
  // Favicon and app icons
  { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
  { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
  { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png" },
  { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16x16.png" },
  { rel: "manifest", href: "/site.webmanifest" },
];

export async function loader() {
  return json({
    initialLang: 'ar',
  });
}

export default function App() {
  const { initialLang } = useLoaderData<typeof loader>();
  
  useEffect(() => {
    // Initialize performance optimizations
    initializeOptimizations();
    
    // Initialize language
    const preferredLanguage = languageDetection.getPreferredLanguage();
    const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    
    if (savedLanguage && ['ar', 'en'].includes(savedLanguage)) {
      document.documentElement.lang = savedLanguage;
      document.documentElement.dir = savedLanguage === 'ar' ? 'rtl' : 'ltr';
    } else if (preferredLanguage !== DEFAULT_LANGUAGE) {
      document.documentElement.lang = preferredLanguage;
      document.documentElement.dir = preferredLanguage === 'ar' ? 'rtl' : 'ltr';
      localStorage.setItem(LANGUAGE_STORAGE_KEY, preferredLanguage);
    }
  }, []);

  return (
    <html lang={initialLang} dir={initialLang === 'ar' ? 'rtl' : 'ltr'}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <script defer src="https://cloud.umami.is/script.js" data-website-id="ba539912-416d-40b9-9475-bf326f47c504"></script>
        
        <script
          dangerouslySetInnerHTML={{
            __html: `
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','GTM-K5BJ3MB9');
            `
          }}
        />

        <meta name="msvalidate.01" content="0065F69BB865A9FEAC57F4DF82D91363" />
        <Analytics/>
        <Meta />
        <Links />
        <style dangerouslySetInnerHTML={{ __html: criticalCSS }} />
      </head>
      <body className="bg-gray-50 dark:bg-dark-gradient font-['Tajawal'] text-gray-800 dark:text-gray-100 transition-all duration-300">
        <ThemeProvider defaultTheme="system">
          <Outlet />
        </ThemeProvider>
        <ScrollRestoration />
        <Scripts />
        <LiveReload />
      
          <noscript>
            <iframe 
              src="https://www.googletagmanager.com/ns.html?id=GTM-K5BJ3MB9" 
              height="0" 
              width="0" 
              style={{ display: 'none', visibility: 'hidden' }}
              title="Google Tag Manager"
            />
          </noscript>

        {/* Person Structured Data - محسن لـ SEO و Sitelinks */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Person",
              "name": "أسامة الزبيدي",
              "alternateName": "Osama Al-Zubaidi",
              "jobTitle": "محلل بيانات، مطور ويب، متخصص رياضيات",
              "description": "محلل بيانات، مطور ويب، متخصص رياضيات. أستطيع تحويل الأرقام إلى ضحك، والكود إلى قهوة! ☕",
              "url": "https://oz.sa",
              "image": "https://oz.sa/logo-light.png",
              "sameAs": [
                "https://twitter.com/osama_zubaidi",
                "https://linkedin.com/in/osama-zubaidi",
                "https://github.com/osama-zubaidi"
              ],
              "knowsAbout": [
                "تحليل البيانات",
                "تطوير الويب",
                "الرياضيات",
                "البرمجة",
                "التطبيقات المجانية",
                "حاسبة العمر",
                "حاسبة الفرق بين التواريخ",
                "تفقيط الأعداد",
                "واتس مباشر",
                "مولد QR",
                "السيرة الذاتية الذكية",
                "بطاقات التهنئة",
                "أذكاري",
                "مولد الفواتير",
                "مواعيد السعودية"
              ],
              "hasOccupation": {
                "@type": "Occupation",
                "name": "محلل بيانات ومطور ويب",
                "description": "متخصص في تحليل البيانات وتطوير تطبيقات الويب"
              },
              "worksFor": {
                "@type": "Organization",
                "name": "أسامة الزبيدي",
                "url": "https://oz.sa"
              },
              "nationality": {
                "@type": "Country",
                "name": "Saudi Arabia"
              },
              "address": {
                "@type": "PostalAddress",
                "addressCountry": "SA",
                "addressRegion": "Saudi Arabia"
              },
              "alumniOf": {
                "@type": "EducationalOrganization",
                "name": "جامعة الملك سعود"
              },
              "award": [
                "مطور ويب محترف",
                "محلل بيانات متخصص",
                "خبير تقني"
              ],
              "knowsLanguage": ["Arabic", "English"],
              "telephone": "+966-50-000-0000",
              "email": "contact@oz.sa",
              // معلومات إضافية لتحسين ظهور "أسامة الزبيدي"
              "brand": {
                "@type": "Brand",
                "name": "أسامة الزبيدي",
                "alternateName": "Osama Al-Zubaidi",
                "description": "محلل بيانات، مطور ويب، متخصص رياضيات",
                "slogan": "أستطيع تحويل الأرقام إلى ضحك، والكود إلى قهوة! ☕",
                "url": "https://oz.sa"
              },
              "hasCredential": [
                "محلل بيانات محترف",
                "مطور ويب متخصص",
                "خبير رياضيات"
              ],
              "hasOfferCatalog": {
                "@type": "OfferCatalog",
                "name": "الأدوات المجانية",
                "itemListElement": [
                  {
                    "@type": "Offer",
                    "itemOffered": {
                      "@type": "WebApplication",
                      "name": "حاسبة العمر",
                      "url": "https://oz.sa/age-calculator"
                    }
                  },
                  {
                    "@type": "Offer",
                    "itemOffered": {
                      "@type": "WebApplication",
                      "name": "أذكاري",
                      "url": "https://oz.sa/adhkar"
                    }
                  }
                ]
              }
            })
          }}
        />
        
        {/* SiteNavigationElement - محسن لـ Sitelinks */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "SiteNavigationElement",
              "name": "القائمة الرئيسية - أسامة الزبيدي",
              "url": "https://oz.sa",
              "hasPart": [
                {
                  "@type": "WebPage",
                  "name": "الرئيسية",
                  "url": "https://oz.sa/",
                  "description": "الصفحة الرئيسية لأسامة الزبيدي"
                },
                {
                  "@type": "WebPage",
                  "name": "الخدمات",
                  "url": "https://oz.sa/services",
                  "description": "جميع الخدمات والأدوات المجانية"
                },
                {
                  "@type": "WebPage",
                  "name": "حاسبة العمر",
                  "url": "https://oz.sa/age-calculator",
                  "description": "حاسبة العمر الذكية بالهجري والميلادي"
                },
                {
                  "@type": "WebPage",
                  "name": "أذكاري",
                  "url": "https://oz.sa/adhkar",
                  "description": "أذكار الصباح والمساء والنوم"
                },
                {
                  "@type": "WebPage",
                  "name": "السيرة الذاتية",
                  "url": "https://oz.sa/resume",
                  "description": "إنشاء السيرة الذاتية الاحترافية"
                },
                {
                  "@type": "WebPage",
                  "name": "من نحن",
                  "url": "https://oz.sa/about",
                  "description": "معلومات عن أسامة الزبيدي"
                },
                {
                  "@type": "WebPage",
                  "name": "اتصل بنا",
                  "url": "https://oz.sa/contact",
                  "description": "تواصل مع أسامة الزبيدي"
                }
              ]
            })
          }}
        />
        
        {/* BreadcrumbList - محسن لـ Sitelinks */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "الرئيسية",
                  "item": "https://oz.sa/"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "الخدمات",
                  "item": "https://oz.sa/services"
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "حاسبة العمر",
                  "item": "https://oz.sa/age-calculator"
                },
                {
                  "@type": "ListItem",
                  "position": 4,
                  "name": "أذكاري",
                  "item": "https://oz.sa/adhkar"
                },
                {
                  "@type": "ListItem",
                  "position": 5,
                  "name": "السيرة الذاتية",
                  "item": "https://oz.sa/resume"
                },
                {
                  "@type": "ListItem",
                  "position": 6,
                  "name": "من نحن",
                  "item": "https://oz.sa/about"
                },
                {
                  "@type": "ListItem",
                  "position": 7,
                  "name": "اتصل بنا",
                  "item": "https://oz.sa/contact"
                }
              ]
            })
          }}
        />
        
        {/* Website Structured Data - محسن لـ Sitelinks */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebSite",
              "name": "أسامة الزبيدي - Osama Al-Zubaidi",
              "url": "https://oz.sa",
              "description": "موقع أسامة الزبيدي الشخصي - محلل بيانات، مطور ويب، متخصص رياضيات. أدوات مجانية متنوعة ومفيدة للجميع.",
              "author": {
                "@type": "Person",
                "name": "أسامة الزبيدي",
                "alternateName": "Osama Al-Zubaidi"
              },
              "publisher": {
                "@type": "Person",
                "name": "أسامة الزبيدي"
              },
              "potentialAction": {
                "@type": "SearchAction",
                "target": {
                  "@type": "EntryPoint",
                  "urlTemplate": "https://oz.sa/search?q={search_term_string}"
                },
                "query-input": "required name=search_term_string"
              },
              "inLanguage": ["ar", "en"],
              "isAccessibleForFree": true,
              "datePublished": "2024-01-01",
              "dateModified": "2024-12-01",
              "copyrightYear": 2024,
              "copyrightHolder": {
                "@type": "Person",
                "name": "أسامة الزبيدي"
              },
              // معلومات إضافية لتحسين ظهور "أسامة الزبيدي"
              "brand": {
                "@type": "Brand",
                "name": "أسامة الزبيدي",
                "alternateName": "Osama Al-Zubaidi",
                "description": "محلل بيانات، مطور ويب، متخصص رياضيات",
                "slogan": "أستطيع تحويل الأرقام إلى ضحك، والكود إلى قهوة! ☕",
                "url": "https://oz.sa"
              },
              "owner": {
                "@type": "Person",
                "name": "أسامة الزبيدي",
                "alternateName": "Osama Al-Zubaidi"
              },
              "creator": {
                "@type": "Person",
                "name": "أسامة الزبيدي",
                "alternateName": "Osama Al-Zubaidi"
              },
              "maintainer": {
                "@type": "Person",
                "name": "أسامة الزبيدي",
                "alternateName": "Osama Al-Zubaidi"
              },
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "SAR",
                "availability": "https://schema.org/InStock"
              },
              // SiteNavigationElement محسن لـ Sitelinks
              "hasPart": [
                {
                  "@type": "SiteNavigationElement",
                  "name": "القائمة الرئيسية",
                  "url": "https://oz.sa",
                  "hasPart": [
                    {
                      "@type": "WebPage",
                      "name": "الرئيسية",
                      "url": "https://oz.sa/",
                      "description": "الصفحة الرئيسية لأسامة الزبيدي"
                    },
                    {
                      "@type": "WebPage",
                      "name": "الخدمات",
                      "url": "https://oz.sa/services",
                      "description": "جميع الخدمات والأدوات المجانية"
                    },
                    {
                      "@type": "WebPage",
                      "name": "حاسبة العمر",
                      "url": "https://oz.sa/age-calculator",
                      "description": "حاسبة العمر الذكية بالهجري والميلادي"
                    },
                    {
                      "@type": "WebPage",
                      "name": "أذكاري",
                      "url": "https://oz.sa/adhkar",
                      "description": "أذكار الصباح والمساء والنوم"
                    },
                    {
                      "@type": "WebPage",
                      "name": "السيرة الذاتية",
                      "url": "https://oz.sa/resume",
                      "description": "إنشاء السيرة الذاتية الاحترافية"
                    },
                    {
                      "@type": "WebPage",
                      "name": "من نحن",
                      "url": "https://oz.sa/about",
                      "description": "معلومات عن أسامة الزبيدي"
                    },
                    {
                      "@type": "WebPage",
                      "name": "اتصل بنا",
                      "url": "https://oz.sa/contact",
                      "description": "تواصل مع أسامة الزبيدي"
                    }
                  ]
                }
              ],
              "mainEntity": {
                "@type": "ItemList",
                "name": "الأدوات المجانية - أسامة الزبيدي",
                "description": "مجموعة من الأدوات المجانية المفيدة",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "item": {
                      "@type": "WebApplication",
                      "name": "حاسبة العمر",
                      "url": "https://oz.sa/age-calculator",
                      "description": "حاسبة العمر بالهجري والميلادي"
                    }
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "item": {
                      "@type": "WebApplication",
                      "name": "حاسبة الفرق بين التواريخ",
                      "url": "https://oz.sa/date-difference",
                      "description": "حاسبة الفرق بين التواريخ"
                    }
                  },
                  {
                    "@type": "ListItem",
                    "position": 3,
                    "item": {
                      "@type": "WebApplication",
                      "name": "تفقيط الأعداد",
                      "url": "https://oz.sa/tafqit",
                      "description": "تحويل الأرقام إلى نصوص"
                    }
                  },
                  {
                    "@type": "ListItem",
                    "position": 4,
                    "item": {
                      "@type": "WebApplication",
                      "name": "واتس مباشر",
                      "url": "https://oz.sa/quick-whats",
                      "description": "إرسال واتساب مباشر"
                    }
                  },
                  {
                    "@type": "ListItem",
                    "position": 5,
                    "item": {
                      "@type": "WebApplication",
                      "name": "مولد QR",
                      "url": "https://oz.sa/qr-generator",
                      "description": "مولد رمز الاستجابة السريعة"
                    }
                  },
                  {
                    "@type": "ListItem",
                    "position": 6,
                    "item": {
                      "@type": "WebApplication",
                      "name": "السيرة الذاتية الذكية",
                      "url": "https://oz.sa/resume",
                      "description": "إنشاء السيرة الذاتية"
                    }
                  },
                  {
                    "@type": "ListItem",
                    "position": 7,
                    "item": {
                      "@type": "WebApplication",
                      "name": "بطاقات التهنئة",
                      "url": "https://oz.sa/greeting-cards",
                      "description": "إنشاء بطاقات التهنئة"
                    }
                  },
                  {
                    "@type": "ListItem",
                    "position": 8,
                    "item": {
                      "@type": "WebApplication",
                      "name": "أذكاري",
                      "url": "https://oz.sa/adhkar",
                      "description": "أذكار الصباح والمساء"
                    }
                  },
                  {
                    "@type": "ListItem",
                    "position": 9,
                    "item": {
                      "@type": "WebApplication",
                      "name": "مولد الفواتير",
                      "url": "https://oz.sa/invoice",
                      "description": "إنشاء فواتير احترافية"
                    }
                  },
                  {
                    "@type": "ListItem",
                    "position": 10,
                    "item": {
                      "@type": "WebApplication",
                      "name": "مواعيد السعودية",
                      "url": "https://oz.sa/saudi-dates",
                      "description": "التواريخ المهمة في السعودية"
                    }
                  }
                ]
              }
            })
          }}
        />
        
        {/* Organization Structured Data - محسن لـ Sitelinks */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "أسامة الزبيدي",
              "alternateName": "Osama Al-Zubaidi",
              "url": "https://oz.sa",
              "logo": "https://oz.sa/logo-light.png",
              "description": "محلل بيانات، مطور ويب، متخصص رياضيات. يوفر أدوات مجانية متنوعة ومفيدة للجميع.",
              "foundingDate": "2024",
              "address": {
                "@type": "PostalAddress",
                "addressCountry": "SA",
                "addressRegion": "Saudi Arabia",
                "addressLocality": "الرياض"
              },
              "contactPoint": {
                "@type": "ContactPoint",
                "contactType": "customer service",
                "availableLanguage": ["Arabic", "English"],
                "email": "contact@oz.sa",
                "telephone": "+966-50-000-0000"
              },
              "sameAs": [
                "https://twitter.com/osama_zubaidi",
                "https://linkedin.com/in/osama-zubaidi",
                "https://github.com/osama-zubaidi"
              ],
              // معلومات إضافية لتحسين ظهور "أسامة الزبيدي"
              "brand": {
                "@type": "Brand",
                "name": "أسامة الزبيدي",
                "alternateName": "Osama Al-Zubaidi",
                "description": "محلل بيانات، مطور ويب، متخصص رياضيات",
                "slogan": "أستطيع تحويل الأرقام إلى ضحك، والكود إلى قهوة! ☕",
                "url": "https://oz.sa"
              },
              "founder": {
                "@type": "Person",
                "name": "أسامة الزبيدي",
                "alternateName": "Osama Al-Zubaidi"
              },
              "owner": {
                "@type": "Person",
                "name": "أسامة الزبيدي",
                "alternateName": "Osama Al-Zubaidi"
              },
              "creator": {
                "@type": "Person",
                "name": "أسامة الزبيدي",
                "alternateName": "Osama Al-Zubaidi"
              },
              "maintainer": {
                "@type": "Person",
                "name": "أسامة الزبيدي",
                "alternateName": "Osama Al-Zubaidi"
              },
              // SiteNavigationElement محسن لـ Sitelinks
              "hasPart": [
                {
                  "@type": "SiteNavigationElement",
                  "name": "القائمة الرئيسية",
                  "url": "https://oz.sa",
                  "hasPart": [
                    {
                      "@type": "WebPage",
                      "name": "الرئيسية",
                      "url": "https://oz.sa/",
                      "description": "الصفحة الرئيسية لأسامة الزبيدي"
                    },
                    {
                      "@type": "WebPage",
                      "name": "الخدمات",
                      "url": "https://oz.sa/services",
                      "description": "جميع الخدمات والأدوات المجانية"
                    },
                    {
                      "@type": "WebPage",
                      "name": "حاسبة العمر",
                      "url": "https://oz.sa/age-calculator",
                      "description": "حاسبة العمر الذكية بالهجري والميلادي"
                    },
                    {
                      "@type": "WebPage",
                      "name": "أذكاري",
                      "url": "https://oz.sa/adhkar",
                      "description": "أذكار الصباح والمساء والنوم"
                    },
                    {
                      "@type": "WebPage",
                      "name": "السيرة الذاتية",
                      "url": "https://oz.sa/resume",
                      "description": "إنشاء السيرة الذاتية الاحترافية"
                    },
                    {
                      "@type": "WebPage",
                      "name": "من نحن",
                      "url": "https://oz.sa/about",
                      "description": "معلومات عن أسامة الزبيدي"
                    },
                    {
                      "@type": "WebPage",
                      "name": "اتصل بنا",
                      "url": "https://oz.sa/contact",
                      "description": "تواصل مع أسامة الزبيدي"
                    }
                  ]
                }
              ],
              "hasOfferCatalog": {
                "@type": "OfferCatalog",
                "name": "الأدوات المجانية - أسامة الزبيدي",
                "description": "مجموعة من الأدوات المجانية المفيدة",
                "itemListElement": [
                  {
                    "@type": "Offer",
                    "itemOffered": {
                      "@type": "WebApplication",
                      "name": "حاسبة العمر",
                      "url": "https://oz.sa/age-calculator",
                      "description": "حاسبة العمر الذكية بالهجري والميلادي"
                    }
                  },
                  {
                    "@type": "Offer",
                    "itemOffered": {
                      "@type": "WebApplication",
                      "name": "حاسبة الفرق بين التواريخ",
                      "url": "https://oz.sa/date-difference",
                      "description": "حاسبة الفرق بين التواريخ"
                    }
                  },
                  {
                    "@type": "Offer",
                    "itemOffered": {
                      "@type": "WebApplication",
                      "name": "تفقيط الأعداد",
                      "url": "https://oz.sa/tafqit",
                      "description": "تحويل الأرقام إلى نصوص"
                    }
                  },
                  {
                    "@type": "Offer",
                    "itemOffered": {
                      "@type": "WebApplication",
                      "name": "واتس مباشر",
                      "url": "https://oz.sa/quick-whats",
                      "description": "إرسال واتساب مباشر"
                    }
                  },
                  {
                    "@type": "Offer",
                    "itemOffered": {
                      "@type": "WebApplication",
                      "name": "مولد QR",
                      "url": "https://oz.sa/qr-generator",
                      "description": "مولد رمز الاستجابة السريعة"
                    }
                  },
                  {
                    "@type": "Offer",
                    "itemOffered": {
                      "@type": "WebApplication",
                      "name": "السيرة الذاتية الذكية",
                      "url": "https://oz.sa/resume",
                      "description": "إنشاء السيرة الذاتية"
                    }
                  },
                  {
                    "@type": "Offer",
                    "itemOffered": {
                      "@type": "WebApplication",
                      "name": "بطاقات التهنئة",
                      "url": "https://oz.sa/greeting-cards",
                      "description": "إنشاء بطاقات التهنئة"
                    }
                  },
                  {
                    "@type": "Offer",
                    "itemOffered": {
                      "@type": "WebApplication",
                      "name": "أذكاري",
                      "url": "https://oz.sa/adhkar",
                      "description": "أذكار الصباح والمساء"
                    }
                  },
                  {
                    "@type": "Offer",
                    "itemOffered": {
                      "@type": "WebApplication",
                      "name": "مولد الفواتير",
                      "url": "https://oz.sa/invoice",
                      "description": "إنشاء فواتير احترافية"
                    }
                  },
                  {
                    "@type": "Offer",
                    "itemOffered": {
                      "@type": "WebApplication",
                      "name": "مواعيد السعودية",
                      "url": "https://oz.sa/saudi-dates",
                      "description": "التواريخ المهمة في السعودية"
                    }
                  }
                ]
              },
              "areaServed": {
                "@type": "Country",
                "name": "Saudi Arabia"
              },
              "serviceArea": {
                "@type": "Country",
                "name": "Worldwide"
              }
            })
          }}
        />
      </body>
    </html>
  );
}
